import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MainService } from '../../app.service';
import {
  ApiKey,
  ApiKeyCreated,
  ApiKeyDelegatesResponse,
  ApiKeyListResponse
} from './api-keys.models';

export const CORPORATE_MAIL_DOMAIN = '@totvs.com.br';

export const normalizeUsername = (value: string | null | undefined): string => {
  const trimmed = (value || '').trim().toLowerCase();

  return trimmed.endsWith(CORPORATE_MAIL_DOMAIN)
    ? trimmed.slice(0, -CORPORATE_MAIL_DOMAIN.length)
    : trimmed;
};

@Injectable({ providedIn: 'root' })
export class ApiKeysService {
  constructor(private http: HttpClient, private mainService: MainService) {}

  private get baseUrl(): string {
    return `${this.mainService.url_v2}/api-keys`;
  }

  list(): Observable<ApiKeyListResponse> {
    return this.http
      .get<ApiKeyListResponse>(this.baseUrl, this.mainService.httpOptions)
      .pipe(catchError(() => throwError(() => new Error('Não foi possível carregar as API Keys.'))));
  }

  create(name: string, owner?: string): Observable<ApiKeyCreated> {
    const body = owner ? { name, owner } : { name };

    return this.http
      .post<ApiKeyCreated>(this.baseUrl, body, this.mainService.httpOptions)
      .pipe(catchError(() => throwError(() => new Error('Não foi possível criar a API Key.'))));
  }

  listDelegates(): Observable<ApiKeyDelegatesResponse> {
    return this.http
      .get<ApiKeyDelegatesResponse>(`${this.baseUrl}/delegates`, this.mainService.httpOptions)
      .pipe(
        catchError(() => throwError(() => new Error('Não foi possível carregar os acessos.')))
      );
  }

  addDelegate(username: string): Observable<ApiKeyDelegatesResponse> {
    return this.http
      .post<ApiKeyDelegatesResponse>(
        `${this.baseUrl}/delegates`,
        { username: normalizeUsername(username) },
        this.mainService.httpOptions
      )
      .pipe(
        catchError((response: unknown) =>
          throwError(() => new Error(ApiKeysService.delegateErrorMessage(response)))
        )
      );
  }

  removeDelegate(username: string): Observable<ApiKeyDelegatesResponse> {
    const normalized = normalizeUsername(username);

    return this.http
      .delete<ApiKeyDelegatesResponse>(
        `${this.baseUrl}/delegates/${encodeURIComponent(normalized)}`,
        this.mainService.httpOptions
      )
      .pipe(catchError(() => throwError(() => new Error('Não foi possível remover o acesso.'))));
  }

  private static delegateErrorMessage(response: unknown): string {
    const status = (response as { status?: number } | null)?.status;

    if (status === 400) {
      return 'Não foi possível conceder: verifique se o usuário informado existe.';
    }

    if (status === 403) {
      return 'Esse usuário já tem permissão de API Keys, então não pode gerenciar as chaves de outra conta. Informe alguém sem essa permissão.';
    }

    return 'Não foi possível conceder o acesso.';
  }

  revoke(key: ApiKey | string): Observable<void> {
    const clientId = typeof key === 'string' ? key : key.clientId;
    return this.http
      .delete<void>(`${this.baseUrl}/${encodeURIComponent(clientId)}`, this.mainService.httpOptions)
      .pipe(catchError(() => throwError(() => new Error('Não foi possível revogar a API Key.'))));
  }
}
